import React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'

// Generic modal with backdrop, header, body, and footer.
//
//   <Modal open={open} onClose={close} title="Frage hinzufügen" maxWidth={560}>
//     <Modal.Body>…</Modal.Body>
//     <Modal.Foot>
//       <Button variant="ghost">Abbrechen</Button>
//       <span className="flex-1"/>
//       <Button variant="primary">Erstellen</Button>
//     </Modal.Foot>
//   </Modal>
//
// The modal renders into document.body. Backdrop click and Escape trigger
// onClose. Pass closeOnBackdrop={false} to disable backdrop close.

export function Modal({
  open,
  onClose,
  title,
  kicker,
  maxWidth = 560,
  closeOnBackdrop = true,
  mobileSheet = false,
  children,
}) {
  const titleId = React.useId()
  const dialogRef = React.useRef(null)
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  React.useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusable = () => Array.from(dialogRef.current?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ) || [])
    const focusFrame = window.requestAnimationFrame(() => {
      if (!dialogRef.current?.contains(document.activeElement)) {
        const first = focusable()[0]
        if (first) first.focus()
        else dialogRef.current?.focus()
      }
    })
    const onKey = event => {
      if (event.key === 'Escape') {
        onCloseRef.current?.()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusable()
      if (!items.length) {
        event.preventDefault()
        dialogRef.current?.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      window.cancelAnimationFrame(focusFrame)
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [open])

  if (!open) return null

  const content = (
    <div
      className="dkg-scroll fixed inset-0 z-[200] overflow-y-auto bg-[rgba(20,18,16,.42)] backdrop-blur-sm"
      onMouseDown={e => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose && onClose()
      }}
    >
      <div
        className={mobileSheet
          ? 'min-h-full flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-[4vh]'
          : 'min-h-full flex items-center justify-center px-4 py-[4vh]'}
        onMouseDown={e => {
          if (closeOnBackdrop && e.target === e.currentTarget) onClose && onClose()
        }}
      >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={'w-full max-h-[94vh] sm:max-h-none bg-white border border-[#e7e5df] shadow-[0_1px_2px_rgba(20,20,16,.06),0_24px_60px_-20px_rgba(20,20,16,.30)] overflow-hidden flex flex-col ' +
          (mobileSheet ? 'rounded-t-[20px] rounded-b-none sm:rounded-[18px]' : 'rounded-[18px]')}
        style={{ maxWidth }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        {(title || kicker) && (
          <header className="flex items-start gap-3 px-[18px] py-4 border-b border-[#e7e5df]">
            <div className="flex-1 min-w-0">
              {kicker && (
                <span className="block text-[10.5px] font-bold tracking-[0.10em] uppercase text-[#e85d1f] mb-0.5">
                  {kicker}
                </span>
              )}
              {title && (
                <h2 id={titleId} className="m-0 text-[16px] font-semibold tracking-tight text-[#1a1816] leading-tight">
                  {title}
                </h2>
              )}
            </div>
            {onClose && (
              <Button variant="ghost" onClick={onClose} aria-label="Schließen" className="!p-0 !w-[44px] !h-[44px] sm:!w-[34px] sm:!h-[34px] !grid !place-items-center">
                <X size={15}/>
              </Button>
            )}
          </header>
        )}
        {children}
      </div>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content
}

// `scroll` caps the body height while the header and footer stay visible.
Modal.Body = function ModalBody({ className = '', scroll = false, maxHeight = '62vh', children }) {
  const scrollCls = scroll ? 'dkg-scroll overflow-y-auto ' : ''
  return (
    <div
      className={'px-[18px] py-4 flex flex-col gap-3 ' + scrollCls + className}
      style={scroll ? { maxHeight } : undefined}
    >
      {children}
    </div>
  )
}

Modal.Foot = function ModalFoot({ className = '', children }) {
  return (
    <footer className={'flex items-center gap-2 px-[18px] py-3 border-t border-[#e7e5df] bg-[#fafaf7] ' + className}>
      {children}
    </footer>
  )
}
